import React, { useCallback, useMemo } from "react"
import styled from "styled-components"
import { Col, Row } from "react-bootstrap"
import { State } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { EMPTY_LINK } from "../../globalStates/ExhibitorDataState"
import { addValidatorsLink } from "../../globalStates/ExhibitorValidation"
import { useLanguageState } from "../../globalStates/LanguageState"
import { addElement, removeElementById } from "../../globalStates/utils"
import { newId } from "../../utils/Id"
import { repositionItem } from "../../utils/Arrays"
import { useLanguageSwitch } from "../../utils/Language"
import Button from "../../components/Button"
import LanguageSwitch from "../languageSwitch"
import LinkSection from "./LinkSection"
import { Link } from "../../globalStates/ExhibitorDataTypes"

const LinksContentsWrapper = styled.div``

const ButtonsBar = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`

function sortLinks(linkStates: State<Link[], Validation>): State<Link, Validation>[] {
    const links = linkStates.map((linkState) => linkState)
    return links.sort((l1, l2) => l1.order.get() - l2.order.get())
}

interface LinksPageProps {
    linksStates: State<Link[], Validation>
    index?: number
    linksLimit: number
}

const LinksContents: React.FC<LinksPageProps> = ({ linksStates, index, linksLimit }) => {
    const branding = useLanguageState().getStrings()
    const orderedLinkStates = useMemo(() => sortLinks(linksStates), [linksStates])
    const isMonoLingual = branding.configuration.monoLingual ? "De" : "En"
    const { currentLanguage, onChangeLanguageHandler } = useLanguageSwitch(isMonoLingual)
    const monoLingual = branding.configuration.monoLingual
    const canAddLink = useMemo(() => {
        if (linksStates.length >= linksLimit) return false
        let valid = true
        linksStates.forEach((linkState) => {
            const text = linkState.text.get()
            const textDe = linkState.textDe.get()
            monoLingual ? (valid = valid && textDe.length >= 0) : (valid = valid && text.length >= 0)
        })
        return valid
    }, [linksLimit, linksStates, monoLingual])

    const onAddLink = useCallback(() => {
        addElement<Link>(linksStates, { ...EMPTY_LINK, id: newId().toString(), order: linksStates.length + 1 })
        addValidatorsLink(linksStates[linksStates.length - 1])
    }, [linksStates])

    const onRemoveLink = useCallback(
        (linkId: string) => {
            removeElementById<Link>(linksStates, linkId)
        },
        [linksStates]
    )

    const onOrderChanged = useCallback(
        (linkId: string, newIndex: number) => {
            const oldIndex = orderedLinkStates.findIndex((linkState) => linkState.id.get() === linkId)
            const repositionedLinkStates = repositionItem(orderedLinkStates, orderedLinkStates[oldIndex], oldIndex, newIndex)
            repositionedLinkStates.forEach((link, index) => {
                if (link.order.get() !== index + 1) link.order.set(index + 1)
            })
        },
        [orderedLinkStates]
    )

    return (
        <LinksContentsWrapper>
            <Row>
                <Col>
                    <LanguageSwitch
                        id={`links_language_switch`}
                        name={`links_languages`}
                        value={currentLanguage}
                        onChange={onChangeLanguageHandler}
                        entityState={linksStates}
                    />
                </Col>
            </Row>

            {orderedLinkStates?.map((linkState, index) => (
                <LinkSection
                    key={linkState.id.get()}
                    linkState={linkState}
                    index={index}
                    currentLanguage={currentLanguage}
                    onOrderChanged={onOrderChanged}
                    onClickRemove={onRemoveLink}
                />
            ))}

            <ButtonsBar>
                <Button
                    id="addLink"
                    caption={branding.configuration.linksPage.btnAddLink}
                    inverse
                    onClick={onAddLink}
                    disabled={!canAddLink}
                />
            </ButtonsBar>
        </LinksContentsWrapper>
    )
}

export default LinksContents
